import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDeal } from "../../lib/store";
import { Card, ConfirmModal } from "../shared";
import { debtApi, authApi, capitalApi } from "../../lib/api";
import { csvCell } from "../../lib/utils";
import { RaiseTimeline } from "../capital/RaiseTimeline";
import { DealSummary } from "../capital/DealSummary";
import { toast } from "sonner";
import * as m from "../../lib/model";
import { CapitalRail } from "../capital/CapitalRail";
import { TeamSection } from "../team/TeamSection";
import { DebtModal, DEBT_STAGES, LENDER_TYPES } from "./DebtModal";
import { DebtRow, DebtDetail } from "./DebtRow";
import { DebtScoreboard, TermCompare } from "./DebtCompare";
import { TermSheets } from "./TermSheets";
import { LenderListSection } from "./LenderListSection";
import { Attachments } from "./Attachments";
import { Landmark, Download, ChevronLeft, ChevronRight, Send } from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);
const CSV = ["lender", "contact", "email", "type", "stage", "amount", "rate", "rateType", "termYears", "amortYears", "closingDays", "recourse", "feesPct", "covenants", "prepay", "collateral", "owner", "nextFollowUp", "notes"];

export default function DebtMarkets({ dealKey, dealName, section, onSection, addSignal = 0 }) {
  const { M, patch, navigate } = useDeal();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("All");
  const [type, setType] = useState("All");
  const [sel, setSel] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [modal, setModal] = useState({ open: false, initial: null });
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState("");
  const [tl, setTl] = useState(null);
  const today = todayStr();
  const ebitda = M.sde || 0;
  const targetDebt = m.loanAmt(M);
  const cfads = m.cfads(M);

  const load = useCallback(() => debtApi.list({ dealKey, dealName }).then(setRows), [dealKey, dealName]);
  useEffect(() => { load().catch(() => toast.error("Could not load lenders")); authApi.users().then(setUsers).catch(() => {}); }, [load]);
  useEffect(() => { capitalApi.timeline({ dealKey, dealName, kind: "debt" }).then(setTl).catch(() => setTl(null)); }, [dealKey, dealName, rows]);
  const seenSignal = React.useRef(addSignal);
  useEffect(() => { if (addSignal !== seenSignal.current) { seenSignal.current = addSignal; setModal({ open: true, initial: null }); } }, [addSignal]);

  const filtered = useMemo(() => rows.filter((l) => (stage === "All" || l.stage === stage) && (type === "All" || l.type === type)
    && (!q || `${l.lender} ${l.contact} ${l.email} ${l.source}`.toLowerCase().includes(q.toLowerCase()))), [rows, stage, type, q]);
  const replace = (l) => setRows((rs) => rs.map((x) => (x.id === l.id ? l : x)));
  const save = async (form) => {
    if (form.id) { replace(await debtApi.patch(form.id, form)); toast.success("Terms saved"); }
    else { await debtApi.create(form); await load(); toast.success(`${form.lender} added`); }
  };
  const remove = async (l) => { await debtApi.remove(l.id); setRows((rs) => rs.filter((x) => x.id !== l.id)); toast.success(`${l.lender} removed`); };
  const importCrm = async () => {
    setBusy("imp");
    try { const r = await debtApi.importLenders({ dealKey, dealName, type: type === "All" ? null : type, limit: 100 }); await load(); toast.success(`Imported ${r.imported} lender(s) (${r.skipped} already tracked)`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(""); }
  };
  const pick = (l) => {
    const amount = +l.amount || 0;
    const years = +l.amortYears || +l.termYears || M.loanYrs;
    if (m.isRecap(M)) patch({ rate: +l.rate, loanYrs: years, recap: { ...(M.recap || {}), newDebt: amount } });
    else {
      const downPct = M.askPrice > 0 ? Math.max(0, Math.min(100, (1 - (amount + (M.snAmt || 0)) / M.askPrice) * 100)) : M.downPct;
      patch({ rate: +l.rate, loanYrs: years, downPct: Math.round(downPct * 10) / 10 });
    }
    toast.success(`${l.lender}'s terms applied — ${m.fmt$(amount)} at ${l.rate}% over ${years} yrs now drive the model`);
    navigate("assumptions");
  };
  const exportCsv = () => {
    const lines = filtered.map((l) => CSV.map((c) => csvCell(l[c])).join(","));
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([[CSV.join(","), ...lines].join("\n")], { type: "text/csv" }));
    a.download = `debt-pipeline-${dealName}.csv`; a.click();
  };
  const followUps = rows.filter((l) => l.nextFollowUp && l.stage !== "Passed" && l.stage !== "Closed");
  const overdue = followUps.filter((l) => l.nextFollowUp < today);

  const sections = [
    { id: "dscoreboard", label: "Debt Scoreboard" },
    { id: "dtimeline", label: "Closing Timeline", badge: tl ? `${tl.daysToClose}d` : null },
    { id: "dpipeline", label: "Lender Pipeline", badge: rows.length || null },
    { id: "dterms", label: "Term Sheets", badge: rows.filter((l) => l.stage !== "Passed" && (l.amount > 0 || (l.attachments || []).length)).length || null },
    { id: "dsummary", label: "Deal Summary" },
    { id: "dcompare", label: "Term Sheet Comparison", badge: rows.filter((l) => l.amount > 0 && l.rate > 0 && l.stage !== "Passed").length || null },
    { id: "dfollowups", label: "Follow-ups Due", badge: overdue.length || null },
    { id: "dlenders", label: "Lender List", badge: null },
    { id: "dteam", label: "Team & Activity" },
  ];
  const active = sections.some((s) => s.id === section) ? section : "dscoreboard";
  const idx = sections.findIndex((x) => x.id === active);
  const committed = rows.filter((l) => ["Committed", "Closed"].includes(l.stage)).reduce((s, l) => s + (+l.amount || 0), 0);
  const metrics = [
    ["Funding in", tl ? `${tl.daysToClose}d` : "—", tl && tl.daysToClose < 0 ? "neg" : ""], ["Debt need", m.fmt$(targetDebt)], ["Leverage", ebitda ? m.fmtX(targetDebt / ebitda) : "—"], ["Lenders", rows.filter((l) => l.stage !== "Passed").length],
    ["Term sheets", rows.filter((l) => ["Term Sheet", "Negotiating", "Committed", "Closed"].includes(l.stage)).length], ["Committed", m.fmt$(committed)], ["Overdue", overdue.length],
  ];
  const expanded = rows.find((l) => l.id === expandedId);

  return (
    <div className="asmp-layout" data-testid="debt-markets">
      <CapitalRail sections={sections} active={active} onSelect={onSection} metrics={metrics} title="Debt sections" liveLabel="Debt · live" />
      <div className="asmp-body" data-testid="debt-section-body">
        <div className="asmp-crumb">
          <span className="ac-idx">{idx + 1} / {sections.length}</span>
          <span className="ac-lbl">{sections[idx].label}</span>
          <div className="ac-nav">
            <button className="btn" disabled={idx <= 0} onClick={() => onSection(sections[idx - 1].id)} data-testid="debt-prev"><ChevronLeft size={13} />Prev</button>
            <button className="btn" disabled={idx >= sections.length - 1} onClick={() => onSection(sections[idx + 1].id)} data-testid="debt-next">Next<ChevronRight size={13} /></button>
          </div>
        </div>

        {active === "dscoreboard" && <DebtScoreboard rows={rows} targetDebt={targetDebt} ebitda={ebitda} cfads={cfads} tl={tl} onOpenTimeline={() => onSection("dtimeline")} />}
        {active === "dtimeline" && <RaiseTimeline kind="debt" dealKey={dealKey} dealName={dealName} target={targetDebt} tl={tl} onChange={setTl} />}
        {active === "dsummary" && <DealSummary M={M} s={null} target={0} tl={tl} room={null} />}
        {active === "dcompare" && <TermCompare rows={rows} targetDebt={targetDebt} ebitda={ebitda} cfads={cfads} onPick={pick} />}
        {active === "dterms" && <TermSheets rows={rows} ebitda={ebitda} cfads={cfads} onEdit={(x) => setModal({ open: true, initial: x })} onChange={replace} onAdd={() => setModal({ open: true, initial: null })}
          onCreate={async (p) => { await debtApi.create({ ...p, dealKey, dealName }); await load(); }} onRemove={setConfirm} />}
        {active === "dlenders" && <LenderListSection dealKey={dealKey} dealName={dealName} tracked={rows} onImported={load} />}
        {active === "dteam" && <TeamSection dealKey={dealKey} dealName={dealName} />}
        {active === "dfollowups" && (
          <Card title={`Lender follow-ups — ${overdue.length} overdue · ${followUps.length} scheduled`} testid="debt-followups">
            {followUps.length === 0 && <div className="cm-empty-block">No lender follow-ups scheduled. Set a next follow-up date on each lender you're chasing.</div>}
            {followUps.sort((a, b) => a.nextFollowUp.localeCompare(b.nextFollowUp)).map((l) => (
              <div key={l.id} className="fu-row" data-testid={`debt-fu-${l.id}`}>
                <span className={`fu-when ${l.nextFollowUp < today ? "neg" : ""}`}>{l.nextFollowUp}</span>
                <b>{l.lender}</b><span className="tag">{l.stage}</span><span className="fu-note">{l.amount ? `${m.fmt$(l.amount)} @ ${l.rate}%` : "no terms yet"}</span>
                <button className="link-btn lime" onClick={() => { onSection("dpipeline"); setExpandedId(l.id); }}>Open →</button>
              </div>
            ))}
          </Card>
        )}

        {active === "dpipeline" && (
          <Card title={`Lender pipeline — ${filtered.length} of ${rows.length} lenders`} testid="debt-pipeline">
            <div className="cm-toolbar">
              <input className="inp cm-search" placeholder="Search lender, contact…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="debt-search" />
              <select className="inp cm-inline" value={stage} onChange={(e) => setStage(e.target.value)} data-testid="debt-stage-filter">{["All", ...DEBT_STAGES].map((s) => <option key={s} value={s}>{s === "All" ? "All stages" : s}</option>)}</select>
              <select className="inp cm-inline" value={type} onChange={(e) => setType(e.target.value)} data-testid="debt-type-filter">{["All", ...LENDER_TYPES].map((s) => <option key={s} value={s}>{s === "All" ? "All lender types" : s}</option>)}</select>
              <button className="btn btn-primary" onClick={() => setModal({ open: true, initial: null })} data-testid="debt-add"><Landmark size={13} />Add lender</button>
              <button className="btn" onClick={importCrm} disabled={busy === "imp"} data-testid="debt-import"><Send size={13} />{busy === "imp" ? "Importing…" : `Import Debt Lenders CRM${type !== "All" ? ` (${type})` : ""}`}</button>
              <button className="btn" onClick={exportCsv} data-testid="debt-export"><Download size={13} />CSV</button>
            </div>
            <div className="cm-table-wrap">
              <table className="tbl inv-tbl" data-testid="debt-table">
                <thead><tr>
                  <th><input type="checkbox" checked={filtered.length > 0 && filtered.every((l) => sel[l.id])} onChange={(e) => setSel(e.target.checked ? Object.fromEntries(filtered.map((l) => [l.id, true])) : {})} /></th>
                  <th>Lender</th><th>Banker</th><th>Type</th><th>Stage</th><th className="num">Amount</th><th className="num">Leverage</th><th className="num">Rate</th><th className="num">Term/Amort</th><th className="num">Close</th><th>Recourse</th><th>Follow-up</th><th /><th />
                </tr></thead>
                <tbody>
                  {filtered.map((l) => (
                    <DebtRow key={l.id} lead={l} ebitda={ebitda} today={today} checked={!!sel[l.id]} expanded={expandedId === l.id}
                      onCheck={() => setSel((x) => ({ ...x, [l.id]: !x[l.id] }))} onExpand={(id) => setExpandedId(expandedId === id ? null : id)}
                      onChange={replace} onEdit={(x) => setModal({ open: true, initial: x })} onRemove={setConfirm} />
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && <div className="cm-empty-block" data-testid="debt-empty">No lenders yet. Add one, or import from the Debt Lenders CRM to start collecting term sheets.</div>}
            </div>
          </Card>
        )}
        {active === "dpipeline" && expanded && <DebtDetail lead={expanded} today={today} dealName={dealName} M={M} onChange={replace} onClose={() => setExpandedId(null)} />}
      </div>

      <DebtModal open={modal.open} initial={modal.initial} dealKey={dealKey} dealName={dealName} ebitda={ebitda} targetDebt={targetDebt} cfads={cfads} users={users}
        onClose={() => setModal({ open: false, initial: null })} onSave={save} />
      <ConfirmModal open={!!confirm} title="Remove lender" danger confirmLabel="Remove" message={confirm ? `Remove ${confirm.lender} from the debt pipeline?` : ""}
        onConfirm={() => { remove(confirm); setConfirm(null); }} onCancel={() => setConfirm(null)} />
    </div>
  );
}
